/**
 * IndexedDB utilities for offline POS transaction storage
 */

import { CreateSalesRequest } from 'types/sales';

// Use separate database for POS transactions to avoid conflicts
const POS_DB_NAME = 'abrasoft_pos_db';
const POS_DB_VERSION = 4; // Incremented to force schema update and fix open failures
const POS_STORE_NAME = 'pos_transactions';
const FLOATING_ORDERS_STORE = 'floating_orders';
const VOIDED_ORDERS_STORE = 'voided_orders';
const COMPLETED_ORDERS_STORE = 'completed_orders';

export interface CartItem {
  productId: string;
  productName: string;
  price: number;
  quantity: number;
}

export interface FloatingOrder {
  id: string; // Local ID
  companyId: string; // Company ID to filter transactions by company
  cart: CartItem[];
  customerId: string;
  customerName?: string;
  subtotal: number;
  createdAt: number;
  updatedAt: number;
}

export interface PendingTransaction {
  id: string; // Local ID (generated client-side)
  companyId: string; // Company ID to filter transactions by company
  syncStatus: 'pending_sync' | 'synced';
  requestData: CreateSalesRequest;
  serverId?: string; // Server ID after successful sync
  createdAt: number;
  syncedAt?: number;
  error?: string;
}

export interface VoidedOrder {
  id: string; // Local ID
  companyId: string; // Company ID to filter transactions by company
  syncStatus: 'pending_sync' | 'synced';
  requestData: CreateSalesRequest;
  serverId?: string; // Server ID if synced before voiding
  voidedAt: number;
  syncedAt?: number;
  error?: string;
  reason?: string;
}

export interface CompletedOrder {
  id: string; // Local ID
  companyId: string; // Company ID to filter transactions by company
  syncStatus: 'pending_sync' | 'synced';
  requestData: CreateSalesRequest;
  serverId?: string; // Server ID after successful sync
  completedAt: number;
  syncedAt?: number;
  error?: string;
  total: number;
  change: number;
}

interface DBInstance {
  db: IDBDatabase | null;
  promise: Promise<IDBDatabase> | null;
}

const dbInstance: DBInstance = {
  db: null,
  promise: null,
};

/**
 * Open IndexedDB database for POS transactions
 */
function openDatabase(): Promise<IDBDatabase> {
  // Return existing database if available
  if (dbInstance.db) {
    return Promise.resolve(dbInstance.db);
  }

  // Return existing promise if database is being opened
  if (dbInstance.promise) {
    return dbInstance.promise;
  }

  // Create new promise to open database
  dbInstance.promise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(POS_DB_NAME, POS_DB_VERSION);

    request.onerror = () => {
      dbInstance.promise = null;
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      dbInstance.db = request.result;
      dbInstance.promise = null;
      resolve(request.result);
    };

    request.onblocked = () => {
      console.warn('POS database upgrade blocked. Please close other tabs.');
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Helper to ensure index exists
      const ensureIndex = (
        store: IDBObjectStore,
        indexName: string,
        keyPath: string,
        options = { unique: false },
      ) => {
        if (!store.indexNames.contains(indexName)) {
          store.createIndex(indexName, keyPath, options);
        }
      };

      // 1. POS Transactions Store
      if (!db.objectStoreNames.contains(POS_STORE_NAME)) {
        const store = db.createObjectStore(POS_STORE_NAME, { keyPath: 'id' });
        ensureIndex(store, 'syncStatus', 'syncStatus');
        ensureIndex(store, 'createdAt', 'createdAt');
        ensureIndex(store, 'companyId', 'companyId');
      } else {
        const store = (
          event.target as IDBOpenDBRequest
        ).transaction!.objectStore(POS_STORE_NAME);
        ensureIndex(store, 'syncStatus', 'syncStatus');
        ensureIndex(store, 'createdAt', 'createdAt');
        ensureIndex(store, 'companyId', 'companyId');
      }

      // 2. Floating Orders Store
      if (!db.objectStoreNames.contains(FLOATING_ORDERS_STORE)) {
        const store = db.createObjectStore(FLOATING_ORDERS_STORE, {
          keyPath: 'id',
        });
        ensureIndex(store, 'createdAt', 'createdAt');
        ensureIndex(store, 'updatedAt', 'updatedAt');
        ensureIndex(store, 'companyId', 'companyId');
      } else {
        const store = (
          event.target as IDBOpenDBRequest
        ).transaction!.objectStore(FLOATING_ORDERS_STORE);
        ensureIndex(store, 'createdAt', 'createdAt');
        ensureIndex(store, 'updatedAt', 'updatedAt');
        ensureIndex(store, 'companyId', 'companyId');
      }

      // 3. Voided Orders Store
      if (!db.objectStoreNames.contains(VOIDED_ORDERS_STORE)) {
        const store = db.createObjectStore(VOIDED_ORDERS_STORE, {
          keyPath: 'id',
        });
        ensureIndex(store, 'syncStatus', 'syncStatus');
        ensureIndex(store, 'voidedAt', 'voidedAt');
        ensureIndex(store, 'companyId', 'companyId');
      } else {
        const store = (
          event.target as IDBOpenDBRequest
        ).transaction!.objectStore(VOIDED_ORDERS_STORE);
        ensureIndex(store, 'syncStatus', 'syncStatus');
        ensureIndex(store, 'voidedAt', 'voidedAt');
        ensureIndex(store, 'companyId', 'companyId');
      }

      // 4. Completed Orders Store
      if (!db.objectStoreNames.contains(COMPLETED_ORDERS_STORE)) {
        const store = db.createObjectStore(COMPLETED_ORDERS_STORE, {
          keyPath: 'id',
        });
        ensureIndex(store, 'syncStatus', 'syncStatus');
        ensureIndex(store, 'completedAt', 'completedAt');
        ensureIndex(store, 'companyId', 'companyId');
      } else {
        const store = (
          event.target as IDBOpenDBRequest
        ).transaction!.objectStore(COMPLETED_ORDERS_STORE);
        ensureIndex(store, 'syncStatus', 'syncStatus');
        ensureIndex(store, 'completedAt', 'completedAt');
        ensureIndex(store, 'companyId', 'companyId');
      }
    };
  });

  return dbInstance.promise;
}

/**
 * Generate a unique local ID for a transaction
 */
function generateLocalId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Store a pending transaction in IndexedDB
 */
export async function storePendingTransaction(
  requestData: CreateSalesRequest,
  companyId: string,
): Promise<string> {
  const db = await openDatabase();
  const transaction = db.transaction([POS_STORE_NAME], 'readwrite');
  const store = transaction.objectStore(POS_STORE_NAME);

  const localId = generateLocalId();
  const pendingTransaction: PendingTransaction = {
    id: localId,
    companyId,
    syncStatus: 'pending_sync',
    requestData,
    createdAt: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const request = store.put(pendingTransaction);

    request.onsuccess = () => {
      resolve(localId);
    };

    request.onerror = () => {
      reject(new Error('Failed to store pending transaction in IndexedDB'));
    };
  });
}

/**
 * Get a pending transaction by ID
 */
export async function getPendingTransaction(
  id: string,
): Promise<PendingTransaction | null> {
  const db = await openDatabase();
  const transaction = db.transaction([POS_STORE_NAME], 'readonly');
  const store = transaction.objectStore(POS_STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result as PendingTransaction | undefined;
      resolve(result || null);
    };

    request.onerror = () => {
      reject(new Error('Failed to get pending transaction from IndexedDB'));
    };
  });
}

/**
 * Get all pending transactions that need to be synced for a specific company
 */
export async function getPendingSyncTransactions(
  companyId: string,
): Promise<PendingTransaction[]> {
  const db = await openDatabase();
  const transaction = db.transaction([POS_STORE_NAME], 'readonly');
  const store = transaction.objectStore(POS_STORE_NAME);
  const companyIndex = store.index('companyId');

  return new Promise((resolve, reject) => {
    const request = companyIndex.getAll(companyId);

    request.onsuccess = () => {
      const results = (request.result as PendingTransaction[]) || [];
      // Filter by pending_sync status and sort by creation date (oldest first)
      const pending = results.filter((t) => t.syncStatus === 'pending_sync');
      pending.sort((a, b) => a.createdAt - b.createdAt);
      resolve(pending);
    };

    request.onerror = () => {
      reject(
        new Error('Failed to get pending sync transactions from IndexedDB'),
      );
    };
  });
}

/**
 * Update transaction sync status
 */
export async function updateTransactionSyncStatus(
  id: string,
  syncStatus: 'pending_sync' | 'synced',
  serverId?: string,
  error?: string,
): Promise<void> {
  const db = await openDatabase();
  const transaction = db.transaction([POS_STORE_NAME], 'readwrite');
  const store = transaction.objectStore(POS_STORE_NAME);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as PendingTransaction | undefined;
      if (!existing) {
        reject(new Error('Transaction not found'));
        return;
      }

      const updated: PendingTransaction = {
        ...existing,
        syncStatus,
        ...(serverId && { serverId }),
        ...(syncStatus === 'synced' && { syncedAt: Date.now() }),
        ...(error && { error }),
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => {
        resolve();
      };

      putRequest.onerror = () => {
        reject(new Error('Failed to update transaction sync status'));
      };
    };

    getRequest.onerror = () => {
      reject(new Error('Failed to get transaction for update'));
    };
  });
}

/**
 * Delete a synced transaction (cleanup)
 */
export async function deleteSyncedTransaction(id: string): Promise<void> {
  const db = await openDatabase();
  const transaction = db.transaction([POS_STORE_NAME], 'readwrite');
  const store = transaction.objectStore(POS_STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to delete synced transaction'));
    };
  });
}

// ==================== FLOATING ORDERS (Pending/Floating) ====================

/**
 * Store a floating order (cart state saved before payment)
 */
export async function storeFloatingOrder(
  cart: CartItem[],
  customerId: string,
  companyId: string,
  customerName?: string,
): Promise<string> {
  const db = await openDatabase();
  const transaction = db.transaction([FLOATING_ORDERS_STORE], 'readwrite');
  const store = transaction.objectStore(FLOATING_ORDERS_STORE);

  const subtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const localId = generateLocalId();
  const floatingOrder: FloatingOrder = {
    id: localId,
    companyId,
    cart,
    customerId,
    customerName,
    subtotal,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const request = store.put(floatingOrder);

    request.onsuccess = () => {
      resolve(localId);
    };

    request.onerror = () => {
      reject(new Error('Failed to store floating order in IndexedDB'));
    };
  });
}

/**
 * Get all floating orders for a specific company
 */
export async function getAllFloatingOrders(
  companyId: string,
): Promise<FloatingOrder[]> {
  const db = await openDatabase();
  const transaction = db.transaction([FLOATING_ORDERS_STORE], 'readonly');
  const store = transaction.objectStore(FLOATING_ORDERS_STORE);
  const companyIndex = store.index('companyId');

  return new Promise((resolve, reject) => {
    const request = companyIndex.getAll(companyId);

    request.onsuccess = () => {
      const results = (request.result as FloatingOrder[]) || [];
      // Sort by updated date (newest first)
      results.sort((a, b) => b.updatedAt - a.updatedAt);
      resolve(results);
    };

    request.onerror = () => {
      reject(new Error('Failed to get floating orders from IndexedDB'));
    };
  });
}

/**
 * Get a floating order by ID
 */
export async function getFloatingOrder(
  id: string,
): Promise<FloatingOrder | null> {
  const db = await openDatabase();
  const transaction = db.transaction([FLOATING_ORDERS_STORE], 'readonly');
  const store = transaction.objectStore(FLOATING_ORDERS_STORE);

  return new Promise((resolve, reject) => {
    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result as FloatingOrder | undefined;
      resolve(result || null);
    };

    request.onerror = () => {
      reject(new Error('Failed to get floating order from IndexedDB'));
    };
  });
}

/**
 * Delete a floating order
 */
export async function deleteFloatingOrder(id: string): Promise<void> {
  const db = await openDatabase();
  const transaction = db.transaction([FLOATING_ORDERS_STORE], 'readwrite');
  const store = transaction.objectStore(FLOATING_ORDERS_STORE);

  return new Promise((resolve, reject) => {
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to delete floating order from IndexedDB'));
    };
  });
}

// ==================== VOIDED ORDERS ====================

/**
 * Store a voided order
 */
export async function storeVoidedOrder(
  order: PendingTransaction | CompletedOrder,
  reason?: string,
): Promise<string> {
  const db = await openDatabase();
  const transaction = db.transaction([VOIDED_ORDERS_STORE], 'readwrite');
  const store = transaction.objectStore(VOIDED_ORDERS_STORE);

  const voidedOrder: VoidedOrder = {
    id: order.id,
    companyId: order.companyId,
    syncStatus: 'pending_sync', // Voided orders need to be synced to server
    requestData: order.requestData,
    serverId: order.serverId,
    voidedAt: Date.now(),
    reason,
  };

  return new Promise((resolve, reject) => {
    const request = store.put(voidedOrder);

    request.onsuccess = () => {
      resolve(order.id);
    };

    request.onerror = () => {
      reject(new Error('Failed to store voided order in IndexedDB'));
    };
  });
}

/**
 * Get all voided orders for a specific company
 */
export async function getAllVoidedOrders(
  companyId: string,
): Promise<VoidedOrder[]> {
  const db = await openDatabase();
  const transaction = db.transaction([VOIDED_ORDERS_STORE], 'readonly');
  const store = transaction.objectStore(VOIDED_ORDERS_STORE);
  const companyIndex = store.index('companyId');

  return new Promise((resolve, reject) => {
    const request = companyIndex.getAll(companyId);

    request.onsuccess = () => {
      const results = (request.result as VoidedOrder[]) || [];
      // Sort by voided date (newest first)
      results.sort((a, b) => b.voidedAt - a.voidedAt);
      resolve(results);
    };

    request.onerror = () => {
      reject(new Error('Failed to get voided orders from IndexedDB'));
    };
  });
}

/**
 * Update voided order sync status
 */
export async function updateVoidedOrderSyncStatus(
  id: string,
  syncStatus: 'pending_sync' | 'synced',
  error?: string,
): Promise<void> {
  const db = await openDatabase();
  const transaction = db.transaction([VOIDED_ORDERS_STORE], 'readwrite');
  const store = transaction.objectStore(VOIDED_ORDERS_STORE);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as VoidedOrder | undefined;
      if (!existing) {
        reject(new Error('Voided order not found'));
        return;
      }

      const updated: VoidedOrder = {
        ...existing,
        syncStatus,
        ...(syncStatus === 'synced' && { syncedAt: Date.now() }),
        ...(error && { error }),
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => {
        resolve();
      };

      putRequest.onerror = () => {
        reject(new Error('Failed to update voided order sync status'));
      };
    };

    getRequest.onerror = () => {
      reject(new Error('Failed to get voided order for update'));
    };
  });
}

// ==================== COMPLETED ORDERS ====================

/**
 * Store a completed order
 */
export async function storeCompletedOrder(
  order: PendingTransaction,
  total: number,
  change: number,
): Promise<string> {
  const db = await openDatabase();
  const transaction = db.transaction([COMPLETED_ORDERS_STORE], 'readwrite');
  const store = transaction.objectStore(COMPLETED_ORDERS_STORE);

  const completedOrder: CompletedOrder = {
    id: order.id,
    companyId: order.companyId,
    syncStatus: order.syncStatus,
    requestData: order.requestData,
    serverId: order.serverId,
    completedAt: Date.now(),
    syncedAt: order.syncedAt,
    error: order.error,
    total,
    change,
  };

  return new Promise((resolve, reject) => {
    const request = store.put(completedOrder);

    request.onsuccess = () => {
      resolve(order.id);
    };

    request.onerror = () => {
      reject(new Error('Failed to store completed order in IndexedDB'));
    };
  });
}

/**
 * Get all completed orders for a specific company
 */
export async function getAllCompletedOrders(
  companyId: string,
): Promise<CompletedOrder[]> {
  const db = await openDatabase();
  const transaction = db.transaction([COMPLETED_ORDERS_STORE], 'readonly');
  const store = transaction.objectStore(COMPLETED_ORDERS_STORE);
  const companyIndex = store.index('companyId');

  return new Promise((resolve, reject) => {
    const request = companyIndex.getAll(companyId);

    request.onsuccess = () => {
      const results = (request.result as CompletedOrder[]) || [];
      // Sort by completed date (newest first)
      results.sort((a, b) => b.completedAt - a.completedAt);
      resolve(results);
    };

    request.onerror = () => {
      reject(new Error('Failed to get completed orders from IndexedDB'));
    };
  });
}

/**
 * Update completed order sync status
 */
export async function updateCompletedOrderSyncStatus(
  id: string,
  syncStatus: 'pending_sync' | 'synced',
  serverId?: string,
  error?: string,
): Promise<void> {
  const db = await openDatabase();
  const transaction = db.transaction([COMPLETED_ORDERS_STORE], 'readwrite');
  const store = transaction.objectStore(COMPLETED_ORDERS_STORE);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as CompletedOrder | undefined;
      if (!existing) {
        reject(new Error('Completed order not found'));
        return;
      }

      const updated: CompletedOrder = {
        ...existing,
        syncStatus,
        ...(serverId && { serverId }),
        ...(syncStatus === 'synced' && { syncedAt: Date.now() }),
        ...(error && { error }),
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => {
        resolve();
      };

      putRequest.onerror = () => {
        reject(new Error('Failed to update completed order sync status'));
      };
    };

    getRequest.onerror = () => {
      reject(new Error('Failed to get completed order for update'));
    };
  });
}

/**
 * Delete a completed order
 */
export async function deleteCompletedOrder(id: string): Promise<void> {
  const db = await openDatabase();
  const transaction = db.transaction([COMPLETED_ORDERS_STORE], 'readwrite');
  const store = transaction.objectStore(COMPLETED_ORDERS_STORE);

  return new Promise((resolve, reject) => {
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to delete completed order from IndexedDB'));
    };
  });
}
